import { http, HttpResponse } from 'msw';
import { mockLands, mockOrganicLedgers } from '../fixtures/lands';
import type { Land, OrganicLedger } from '@trace/shared/types';

let landsStore = [...mockLands];
let ledgersStore = [...mockOrganicLedgers];

export const landsHandlers = [
  // List Lands
  http.get('/api/lands', ({ request }) => {
    const url = new URL(request.url);
    const keyword = url.searchParams.get('keyword')?.toLowerCase();
    const status = url.searchParams.get('status');
    const variety = url.searchParams.get('variety');
    const baseId = url.searchParams.get('baseId');
    const enterpriseId = url.searchParams.get('enterpriseId');

    let filtered = [...landsStore];

    if (baseId && baseId !== 'all') {
      filtered = filtered.filter((l) => String((l as any).baseId) === String(baseId));
    }

    if (enterpriseId && enterpriseId !== 'all') {
      filtered = filtered.filter((l) => String((l as any).enterpriseId) === String(enterpriseId));
    }

    if (keyword) {
      filtered = filtered.filter(
        (l) =>
          l.name.toLowerCase().includes(keyword) ||
          l.code.toLowerCase().includes(keyword) ||
          l.manager.toLowerCase().includes(keyword) ||
          ((l as any).enterpriseName && (l as any).enterpriseName.toLowerCase().includes(keyword)),
      );
    }

    if (status && status !== 'all') {
      filtered = filtered.filter((l) => l.status === status);
    }

    if (variety && variety !== 'all') {
      filtered = filtered.filter((l) => l.varieties.includes(variety));
    }

    return HttpResponse.json({
      code: 200,
      message: 'success',
      data: {
        list: filtered,
        total: filtered.length,
        page: 1,
        pageSize: 50,
      },
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  // Get Single Land
  http.get('/api/lands/:id', ({ params }) => {
    const land = landsStore.find((l) => String(l.id) === String(params.id));
    if (!land) {
      return HttpResponse.json(
        { code: 404, message: '地块不存在', success: false, timestamp: new Date().toISOString() },
        { status: 404 },
      );
    }
    return HttpResponse.json({
      code: 200,
      message: 'success',
      data: land,
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  // Create Land
  http.post('/api/lands', async ({ request }) => {
    const body = (await request.json()) as Partial<Land>;
    const newLand: Land = {
      id: `land_${Date.now()}`,
      code: body.code || `LAND-2026-${String(landsStore.length + 1).padStart(3, '0')}`,
      name: body.name || '新地块',
      area: body.area || 100,
      manager: body.manager || '李春山',
      contactPhone: body.contactPhone || '13900139001',
      address: body.address || '黑龙江省五常市有机种植基地',
      soilType: body.soilType || '弱碱性草甸黑钙土',
      waterSource: body.waterSource || '拉林河天然自流活水',
      varieties: body.varieties || ['五优稻4号 (稻花香2号)'],
      status: body.status || 'active',
      boundary: body.boundary || {
        center: { lng: 127.1524, lat: 44.9281 },
        points: [
          { lng: 127.1501, lat: 44.9265 },
          { lng: 127.1552, lat: 44.9272 },
          { lng: 127.1548, lat: 44.9301 },
          { lng: 127.1498, lat: 44.9295 },
        ],
      },
      certificates: body.certificates || [],
      soilReportUrl: body.soilReportUrl || '',
      soilReportName: body.soilReportName || '',
      soilReportDate: body.soilReportDate || '',
      soilReportAgency: body.soilReportAgency || '',
      soilReportResult: body.soilReportResult || '',
      soilReportSize: body.soilReportSize || 0,
      waterReportUrl: body.waterReportUrl || '',
      waterReportName: body.waterReportName || '',
      waterReportDate: body.waterReportDate || '',
      waterReportAgency: body.waterReportAgency || '',
      waterReportResult: body.waterReportResult || '',
      waterReportSize: body.waterReportSize || 0,
      lastSyncTime: new Date().toISOString(),
      syncStatus: 'synced',
      remarks: body.remarks || '',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      ...body,
    };

    landsStore = [newLand, ...landsStore];

    return HttpResponse.json({
      code: 200,
      message: '地块创建成功',
      data: newLand,
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  // Update Land
  http.put('/api/lands/:id', async ({ params, request }) => {
    const body = (await request.json()) as Partial<Land>;
    const idx = landsStore.findIndex((l) => String(l.id) === String(params.id));
    if (idx === -1) {
      return HttpResponse.json(
        { code: 404, message: '地块不存在', success: false, timestamp: new Date().toISOString() },
        { status: 404 },
      );
    }

    landsStore[idx] = {
      ...landsStore[idx]!,
      ...body,
      updatedAt: new Date().toISOString(),
    };

    return HttpResponse.json({
      code: 200,
      message: '地块信息更新成功',
      data: landsStore[idx],
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  // Delete Land
  http.delete('/api/lands/:id', ({ params }) => {
    landsStore = landsStore.filter((l) => String(l.id) !== String(params.id));
    return HttpResponse.json({
      code: 200,
      message: '地块已删除',
      data: null,
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  // Sync Farm IoT Data
  http.post('/api/lands/sync', () => {
    const now = new Date().toISOString();
    landsStore = landsStore.map((l) => ({
      ...l,
      lastSyncTime: now,
      syncStatus: 'synced',
    }));

    return HttpResponse.json({
      code: 200,
      message: '农场数据同步完成',
      data: {
        lastSyncTime: now,
        syncedLandsCount: landsStore.length,
        status: 'success',
      },
      success: true,
      timestamp: now,
    });
  }),

  // Batch Import Lands
  http.post('/api/lands/import', async ({ request }) => {
    const body = (await request.json()) as { items: Partial<Land>[] };
    const importedList: Land[] = (body.items || []).map((item, idx) => ({
      id: `land_imp_${Date.now()}_${idx}`,
      code: item.code || `LAND-2026-IMP${idx + 1}`,
      name: item.name || `导入地块 #${idx + 1}`,
      area: item.area || 100,
      manager: item.manager || '李春山',
      contactPhone: item.contactPhone || '13900139001',
      address: item.address || '五常有机示范基地',
      soilType: item.soilType || '弱碱性黑钙土',
      waterSource: item.waterSource || '拉林河水系',
      varieties: item.varieties || ['五优稻4号'],
      status: item.status || 'active',
      boundary: {
        center: { lng: 127.1524 + idx * 0.005, lat: 44.9281 },
        points: [
          { lng: 127.1501 + idx * 0.005, lat: 44.9265 },
          { lng: 127.1552 + idx * 0.005, lat: 44.9272 },
          { lng: 127.1548 + idx * 0.005, lat: 44.9301 },
          { lng: 127.1498 + idx * 0.005, lat: 44.9295 },
        ],
      },
      certificates: [],
      lastSyncTime: new Date().toISOString(),
      syncStatus: 'synced',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }));

    landsStore = [...importedList, ...landsStore];

    return HttpResponse.json({
      code: 200,
      message: `成功批量导入 ${importedList.length} 块地块`,
      data: {
        successCount: importedList.length,
        failureCount: 0,
        totalCount: importedList.length,
        items: importedList,
      },
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  // Certificates list
  http.get('/api/certificates', ({ request }) => {
    const url = new URL(request.url);
    const keyword = url.searchParams.get('keyword')?.toLowerCase();
    const landId = url.searchParams.get('landId');
    const certType = url.searchParams.get('certType');
    const status = url.searchParams.get('status');

    let allCerts = landsStore.flatMap((l) =>
      (l.certificates || []).map((c) => ({
        ...c,
        name: (c as any).name || c.certName,
        landName: l.name,
        landId: l.id,
        certType: (c as any).certType || 'organic',
        certifiedCrop: (c as any).certifiedCrop || '有机水稻',
        certifiedArea: (c as any).certifiedArea || l.area,
        issueDate: (c.issueDate || '2024-09-01').split('T')[0],
        expiryDate: (c.expiryDate || '2027-08-31').split('T')[0],
      })),
    );

    if (keyword) {
      allCerts = allCerts.filter(
        (c) =>
          c.name?.toLowerCase().includes(keyword) ||
          c.certNumber?.toLowerCase().includes(keyword) ||
          c.issuer?.toLowerCase().includes(keyword),
      );
    }
    if (landId && landId !== 'all') {
      allCerts = allCerts.filter((c) => String(c.landId) === String(landId));
    }
    if (certType && certType !== 'all') {
      allCerts = allCerts.filter((c) => c.certType === certType);
    }
    if (status && status !== 'all') {
      allCerts = allCerts.filter((c) => c.status === status);
    }

    return HttpResponse.json({
      code: 200,
      message: 'success',
      data: {
        list: allCerts,
        total: allCerts.length,
        page: 1,
        pageSize: 50,
      },
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  // Create Certificate
  http.post('/api/certificates', async ({ request }) => {
    const body = (await request.json()) as any;
    const newCert = {
      id: `cert_${Date.now()}`,
      certNumber: body.certNumber || `100OP${Date.now()}`,
      certName: body.name || body.certName || '有机产品认证证书',
      name: body.name || body.certName || '有机产品认证证书',
      issuer: body.issuer || '北京爱科赛尔认证中心',
      issueDate: body.issueDate || '2024-09-01',
      expiryDate: body.expiryDate || '2027-08-31',
      status: body.status || 'valid',
      fileUrl: body.fileUrl || 'https://images.unsplash.com/photo-1606857521015-7f9fcf423740?w=800',
      fileType: 'image',
      landId: body.landId,
      landName: body.landName || '五常核心有机基地',
      certifiedCrop: body.certifiedCrop || '有机水稻',
      certifiedArea: body.certifiedArea || 120,
      certType: body.certType || 'organic',
      remarks: body.remarks || '',
    };

    if (body.landId) {
      const land = landsStore.find((l) => String(l.id) === String(body.landId));
      if (land) {
        land.certificates = [...(land.certificates || []), newCert as any];
      }
    } else if (landsStore[0]) {
      landsStore[0].certificates = [...(landsStore[0].certificates || []), newCert as any];
    }

    return HttpResponse.json({
      code: 200,
      message: '有机资质证书已成功归档入库',
      data: newCert,
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  // Update Certificate
  http.put('/api/certificates/:id', async ({ params, request }) => {
    const body = (await request.json()) as any;
    for (const land of landsStore) {
      const certIdx = (land.certificates || []).findIndex((c) => String(c.id) === String(params.id));
      if (certIdx !== -1) {
        land.certificates[certIdx] = {
          ...land.certificates[certIdx],
          ...body,
          name: body.name || land.certificates[certIdx]!.certName,
          certName: body.name || land.certificates[certIdx]!.certName,
        } as any;
        return HttpResponse.json({
          code: 200,
          message: '证书信息已更新',
          data: land.certificates[certIdx],
          success: true,
          timestamp: new Date().toISOString(),
        });
      }
    }

    return HttpResponse.json({
      code: 200,
      message: '证书信息已更新',
      data: body,
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  // Delete Certificate
  http.delete('/api/certificates/:id', ({ params }) => {
    for (const land of landsStore) {
      if (land.certificates) {
        land.certificates = land.certificates.filter((c) => String(c.id) !== String(params.id));
      }
    }

    return HttpResponse.json({
      code: 200,
      message: '证书已成功删除',
      data: null,
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  // MinIO File Upload Mock Handler
  http.post('/api/files/upload', async ({ request }) => {
    let filename = '有机生产追溯台账原件.pdf';
    let folder = 'ledgers';
    let size = 2450000;
    let contentType = 'application/pdf';

    try {
      const formData = await request.formData();
      const file = formData.get('file') as File | null;
      if (file && typeof file === 'object' && file.name) {
        filename = file.name;
        size = file.size || 2450000;
        contentType = file.type || 'application/pdf';
      }
      const folderParam = formData.get('folder');
      if (folderParam) {
        folder = String(folderParam);
      }
    } catch {}

    const ext = filename.split('.').pop()?.toLowerCase() || 'pdf';
    const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const objectName = `${folder}/${dateStr}/ledger_${Date.now()}.${ext}`;
    const url = 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=1200';

    return HttpResponse.json({
      code: 200,
      message: '文件已成功上传至 MinIO 对象存储',
      data: {
        url,
        objectName,
        originalFilename: filename,
        fileSize: size,
        contentType,
        storageType: 'minio',
      },
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  // MinIO File Download Mock Handler
  http.get('/api/files/download', ({ request }) => {
    const url = new URL(request.url);
    const filename = url.searchParams.get('filename') || '有机生产追溯台账.pdf';
    const objectName = url.searchParams.get('objectName') || url.searchParams.get('fileUrl') || '';
    const content = `=== 工程北米 · 数字有机农产品溯源系统 ===\n\n【文件名称】: ${filename}\n【存储对象】: ${objectName}\n【归档时间】: ${new Date().toISOString()}\n【存储后端】: MinIO S3 Object Storage Cluster (Bucket: trace-system)\n【安全哈希】: SHA-256 e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855\n【合规认证】: 国家有机产品认证 GB/T 19630-2019 / 欧盟有机农业条例 (EOS)\n\n本凭证由五常/嫩江数字农业气象与田间物联网实时审计锁定，具有全流程防伪溯源效力。`;
    const blob = new Blob([content], { type: 'application/octet-stream' });

    return new HttpResponse(blob, {
      headers: {
        'Content-Type': 'application/octet-stream',
        'Content-Disposition': `attachment; filename="${encodeURIComponent(filename)}"`,
      },
    });
  }),

  // ================= 8. 有机生产追溯台账归档 Mock 接口 =================
  // Stats
  http.get('/api/organic-ledgers/stats', () => {
    const totalCount = ledgersStore.length;
    const compliantCount = ledgersStore.filter((l) => l.auditStatus === 'compliant').length;
    const pendingCount = ledgersStore.filter((l) => l.auditStatus === 'pending' || l.auditStatus === 'rectifying').length;
    const coveredLandsCount = new Set(ledgersStore.map((l) => String(l.landId))).size;
    const recentCount = ledgersStore.length;
    const complianceRate = totalCount > 0 ? `${((compliantCount * 100) / totalCount).toFixed(1)}%` : '100.0%';

    return HttpResponse.json({
      code: 200,
      message: 'success',
      data: {
        totalCount,
        compliantCount,
        pendingCount,
        coveredLandsCount,
        recentCount,
        complianceRate,
      },
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  // List Ledgers with Land & Time filtering
  http.get('/api/organic-ledgers', ({ request }) => {
    const url = new URL(request.url);
    const keyword = url.searchParams.get('keyword')?.toLowerCase();
    const landId = url.searchParams.get('landId');
    const startDate = url.searchParams.get('startDate');
    const endDate = url.searchParams.get('endDate');
    const ledgerType = url.searchParams.get('ledgerType');
    const auditStatus = url.searchParams.get('auditStatus');

    let filtered = [...ledgersStore];

    if (landId && landId !== 'all' && landId !== 'null' && landId !== 'undefined') {
      filtered = filtered.filter((l) => {
        if (String(l.landId) === String(landId)) return true;
        if (l.landCode && String(l.landCode).toLowerCase() === String(landId).toLowerCase()) return true;
        const matchedLand = landsStore.find((land) => String(land.id) === String(landId) || String(land.code) === String(landId));
        if (matchedLand && (String(l.landId) === String(matchedLand.id) || l.landCode === matchedLand.code)) return true;
        return false;
      });
    }

    if (startDate && startDate !== 'all') {
      filtered = filtered.filter((l) => (l.recordDate || '').slice(0, 10) >= startDate);
    }

    if (endDate && endDate !== 'all') {
      filtered = filtered.filter((l) => (l.recordDate || '').slice(0, 10) <= endDate);
    }

    if (ledgerType && ledgerType !== 'all') {
      filtered = filtered.filter((l) => l.ledgerType === ledgerType);
    }

    if (auditStatus && auditStatus !== 'all') {
      filtered = filtered.filter((l) => l.auditStatus === auditStatus);
    }

    if (keyword) {
      filtered = filtered.filter(
        (l) =>
          l.ledgerName?.toLowerCase().includes(keyword) ||
          l.ledgerCode?.toLowerCase().includes(keyword) ||
          l.landName?.toLowerCase().includes(keyword) ||
          l.operator?.toLowerCase().includes(keyword) ||
          l.auditor?.toLowerCase().includes(keyword) ||
          l.fileName?.toLowerCase().includes(keyword),
      );
    }

    // 补齐地块名称
    filtered = filtered.map((l) => {
      const land = landsStore.find((land) => String(land.id) === String(l.landId));
      return {
        ...l,
        landName: l.landName || land?.name || '五常核心有机基地 · 1号稻田A区',
        landCode: l.landCode || land?.code || 'LAND-2026-A01',
      };
    });

    return HttpResponse.json({
      code: 200,
      message: 'success',
      data: {
        list: filtered,
        total: filtered.length,
        page: 1,
        pageSize: 50,
      },
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  // Get Single Ledger
  http.get('/api/organic-ledgers/:id', ({ params }) => {
    const item = ledgersStore.find((l) => String(l.id) === String(params.id));
    if (!item) {
      return HttpResponse.json(
        { code: 404, message: '台账不存在', success: false, timestamp: new Date().toISOString() },
        { status: 404 },
      );
    }
    return HttpResponse.json({
      code: 200,
      message: 'success',
      data: item,
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  // Create Ledger
  http.post('/api/organic-ledgers', async ({ request }) => {
    const body = (await request.json()) as Partial<OrganicLedger>;
    const land = landsStore.find((l) => String(l.id) === String(body.landId));

    const newLedger: any = {
      id: `ledger_${Date.now()}`,
      baseId: body.baseId || land?.baseId || 3001,
      baseName: body.baseName || land?.baseName || '五常市龙凤山水源地国家级有机水稻种植示范基地',
      landId: body.landId || 'land_01',
      landName: body.landName || land?.name || '五常核心有机基地 · 1号稻田A区',
      landCode: body.landCode || land?.code || 'LAND-2026-A01',
      ledgerName: body.ledgerName || '新建有机生产台账',
      ledgerCode: body.ledgerCode || `LEDGER-2026-${String(ledgersStore.length + 1).padStart(3, '0')}`,
      ledgerType: body.ledgerType || 'farming',
      recordDate: body.recordDate || new Date().toISOString().split('T')[0],
      periodStart: body.periodStart || new Date().toISOString().split('T')[0],
      periodEnd: body.periodEnd || new Date().toISOString().split('T')[0],
      fileUrl: body.fileUrl || 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=1200',
      fileName: body.fileName || '生产追溯台账原件.pdf',
      fileSizeBytes: body.fileSizeBytes || 2048000,
      fileType: body.fileType || 'pdf',
      operator: body.operator || '李春山',
      auditor: body.auditor || '张建华',
      auditStatus: body.auditStatus || 'compliant',
      organicStandard: body.organicStandard || 'GB/T 19630-2019',
      remarks: body.remarks || '',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    ledgersStore = [newLedger, ...ledgersStore];

    return HttpResponse.json({
      code: 200,
      message: '有机生产台账已成功归档入库',
      data: newLedger,
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  // Update Ledger
  http.put('/api/organic-ledgers/:id', async ({ params, request }) => {
    const body = (await request.json()) as Partial<OrganicLedger>;
    const idx = ledgersStore.findIndex((l) => String(l.id) === String(params.id));
    if (idx === -1) {
      return HttpResponse.json(
        { code: 404, message: '台账不存在', success: false, timestamp: new Date().toISOString() },
        { status: 404 },
      );
    }

    ledgersStore[idx] = {
      ...ledgersStore[idx],
      ...body,
      updatedAt: new Date().toISOString(),
    };

    return HttpResponse.json({
      code: 200,
      message: '有机台账归档信息已更新',
      data: ledgersStore[idx],
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  // Delete Ledger
  http.delete('/api/organic-ledgers/:id', ({ params }) => {
    ledgersStore = ledgersStore.filter((l) => String(l.id) !== String(params.id));
    return HttpResponse.json({
      code: 200,
      message: '有机台账记录已删除',
      data: null,
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),
];
